import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {setupAccountsUrl} from '../../../../helpers/accountHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsMetricsKeys,
  AnalyticsTableMetricsEnum,
  RAnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {getAnalyticsSecondaryMetricIsLoading} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {helpCenterUrl} from '../../../../store/stateless/navigationActions';
import {BasicReportProps, ReportElementIdsEnum} from '../../../helpers/analyticsReportHelpers';
import {useAnalyticsUpdateSelectedMetrics} from '../../../helpers/hooks/useAnalyticsUpdateSelectedMetrics';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {defaultEmptyStateRenderer, EmptyStateRenderer} from '../../reportElements/charts/emptyState';
import {SetupEmptyState} from '../../reportElements/charts/setupEmptyState';
import {
  MetricSelectionDropdown,
  MetricWithOptionalRenderer,
} from '../../reportElements/elementSettings/metricSelectionDropdown';
import {ReportTable} from '../../reportElements/table/reportTable';
import {UpsellPill} from '../../reportElements/upsell/upsellPill';

/*
 * Styles.
 */

const StyledText = styled.span`
  margin-right: 8px;
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledMetricsSelectionDiv = styled.div`
  margin-right: 8px;
  display: flex;
`;

/*
 * Props.
 */

interface CustomerReportTableProps extends BasicReportProps {
  hasCsatSurveysFeature: boolean;
  hasAccountsAnalyticsFeature: boolean;
}

interface TableConfiguration {
  defaultColumnIds: Array<string>;
  blockedColumnIds: Array<string>;
  disabledColumnIds: Array<string>;
  reportId: ReportElementIdsEnum;
}
type TableConfigurations = Partial<Record<AnalyticsTableMetricsEnum, TableConfiguration>>;

/*
 * Component.
 */

export const CustomerReportTable: FC<CustomerReportTableProps> = (props) => {
  const theme = useUserTheme();
  const {isLoading, hasCsatSurveysFeature, hasAccountsAnalyticsFeature} = props;
  const [selectedTableMetric, setSelectedTableMetric] = useState(AnalyticsTableMetricsEnum.ACCOUNTS_TABLE);
  const {updateSelectedMetrics, maybeFetchHiddenMetrics} = useAnalyticsUpdateSelectedMetrics();
  const trackAnalytics = useAnalyticsTracking();
  const tableMetrics = computeTableMetricRenderers(hasAccountsAnalyticsFeature);
  const isSecondaryMetricLoading = useAppSelector(getAnalyticsSecondaryMetricIsLoading);

  const fetchSetAndTrackTableMetric = (selection: AnalyticsMetricsKeys) => {
    if (!RAnalyticsTableMetricsEnum.is(selection)) {
      throw new Error(`Invalid selection. Selection: ${selection} must be a table metric`);
    }

    setSelectedTableMetric(selection);
    maybeFetchHiddenMetrics(selection); // Will fetch the metrics if not already present.
    updateSelectedMetrics([selection], ReportElementIdsEnum.CX_CUSTOMERS_TABLE_V2);

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_SELECT, {
      metric_type: selection,
      report_section: ReportElementIdsEnum.CX_CUSTOMERS_TABLE_V2,
    });
  };

  // TODO: Get columns from the metric
  const tableConfiguration: TableConfigurations = {
    [AnalyticsTableMetricsEnum.ACCOUNTS_TABLE]: {
      defaultColumnIds: [
        'num_active_segments',
        'num_new_segments',
        'num_messages_received',
        'avg_reply_time',
        'sla_breaches',
      ],
      blockedColumnIds: [],
      disabledColumnIds: [hasCsatSurveysFeature ? '' : 'avg_csat_survey_score'],
      reportId: ReportElementIdsEnum.CX_TABLE,
    },
    [AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2]: {
      defaultColumnIds: [
        'num_active_segments',
        'num_new_segments',
        'num_messages_received',
        'avg_reply_time',
        'sla_breaches',
      ],
      blockedColumnIds: [],
      disabledColumnIds: [hasCsatSurveysFeature ? '' : 'avg_csat_survey_score'],
      reportId: ReportElementIdsEnum.CX_TABLE,
    },
  };

  const configuration = tableConfiguration[selectedTableMetric];
  const isAccountTableSelected = selectedTableMetric === AnalyticsTableMetricsEnum.ACCOUNTS_TABLE;

  if (!configuration) {
    throw new Error(`Not a valid table configuration metric: ${selectedTableMetric}`);
  }

  return (
    <>
      {/* Customer experience tables */}
      <ReportTable
        renderDropdown={() => (
          <StyledMetricsSelectionDiv>
            <StyledText color={theme.greys.shade70}>View by</StyledText>
            <MetricSelectionDropdown
              metricsWithOptionalRenderer={tableMetrics}
              setSelectedMetric={fetchSetAndTrackTableMetric}
              selectedMetric={selectedTableMetric}
            />
          </StyledMetricsSelectionDiv>
        )}
        reportSectionId={configuration.reportId}
        isLoading={isLoading || isSecondaryMetricLoading(selectedTableMetric)}
        metric={selectedTableMetric}
        defaultColumnIds={configuration.defaultColumnIds}
        blockedColumnIds={configuration.blockedColumnIds}
        disabledColumnIds={configuration.disabledColumnIds}
        renderEmptyState={emptyStateRenderer(isAccountTableSelected)}
        disableSelection
        initialState={{
          sortBy: [
            {
              id: 'num_active_segments',
              desc: true,
            },
          ],
        }}
      />
    </>
  );
};

/*
 * Helpers.
 */

const emptyStateRenderer = (isAccountsTableSelected: boolean) => {
  if (!isAccountsTableSelected) {
    return defaultEmptyStateRenderer;
  }

  const accountsTableEmptyStateRenderer: EmptyStateRenderer = ({width, height}) => (
    <SetupEmptyState
      width={width}
      height={height}
      setupLink={setupAccountsUrl}
      title={<Trans id="analytics-empty-state-accounts-title">Accounts not set up</Trans>}
      buttonText={<Trans id="analytics-accounts-empty-state-no-rule-button">Set up Accounts</Trans>}
      learnMoreLink={`${helpCenterUrl}/t/h7h1h2s/understanding-front-accounts`}
      interactionId="accounts-analytics-table-empty-set-up-rule"
    />
  );

  return accountsTableEmptyStateRenderer;
};

const computeTableMetricRenderers = (hasAccountsAnalyticsFeature: boolean) => {
  const metricAndRenderer: MetricWithOptionalRenderer[] = [
    {
      metric: AnalyticsTableMetricsEnum.ACCOUNTS_TABLE,
      isDisabled: !hasAccountsAnalyticsFeature,
      customRender: !hasAccountsAnalyticsFeature ? () => <UpsellPill>SCALE</UpsellPill> : undefined,
    },
    {
      metric: AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2,
      isDisabled: false,
      customRender: undefined,
    },
  ];

  return metricAndRenderer;
};
